/**
 * Real-time notifications client. WS at
 * /notifications/ws/{user_id}?token=<jwt>; the server derives the
 * canonical user_id from the JWT `sub` (URL path segment is discarded to
 * prevent impersonation) but the route signature still requires it.
 * Mirrors ChatWidget's WS pattern: `cm_token` storage key, VITE_API_URL
 * fallback, direct :8000 in dev (vite proxy doesn't bridge ws://).
 * Resilience: exponential-backoff reconnect (1s → 30s, reset on open);
 * closes on tab-hidden / reopens on visible; unsubscribe tears down
 * socket + listeners + timers.
 */

// Payload contract — mirrors RealtimeNotificationManager.send_notification
// (backend/services/notifications/realtime.py): top-level `type`,
// `timestamp`, `user_id` + spread per-call payload (`title`,
// `message`/`body`, `priority`, `category`, deep-link fields). Type stays
// permissive; unknown `type`s render generically — backend templates.py
// doesn't emit explicit names yet.
export interface NotificationPayload {
  /** Server-assigned notification ID. May be omitted by some legacy events. */
  id?: string;
  /** Notification type — drives the UI deep-link/CTA. Unknown types render generically. */
  type?: string;
  /** Optional human-readable title (preferred over `message` for the bell list). */
  title?: string;
  /** Optional human-readable body. Some backend paths use `message` instead. */
  body?: string;
  /** Backend's RealtimeNotificationManager spreads the payload, so the body field is `message`. */
  message?: string;
  /** Optional structured payload (event_id, slug, etc.). Untyped on purpose. */
  data?: Record<string, unknown>;
  /** ISO-8601 timestamp from the server. */
  created_at?: string;
  /** Server-assigned timestamp (older code path). */
  timestamp?: string;
  /** Server may include a category ('system', 'chat', 'deal', …). */
  category?: string;
  /** Server may include a priority ('low' | 'medium' | 'high'). */
  priority?: string;
  /** Anything else — keeps the type permissive without `any`. */
  [key: string]: unknown;
}

// --- WS URL builder ---------------------------------------------------------
// Same logic as ChatWidget.buildChatWsUrl (line 35-49). Centralised here so
// future channels can reuse it via a shared helper if we need.
function buildNotificationsWsUrl(userId: string, token: string): string | null {
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  let baseHttp: string;
  if (apiUrl && /^https?:/i.test(apiUrl)) {
    baseHttp = apiUrl.replace(/\/$/, '');
  } else if (typeof window !== 'undefined') {
    // Local dev: vite's devServer.proxy only bridges /api + /health, not ws://,
    // so hit FastAPI directly on :8000. In prod we rely on the absolute URL.
    baseHttp = `${window.location.protocol}//${window.location.hostname}:8000`;
  } else {
    return null;
  }
  const wsBase = baseHttp.replace(/^http/i, 'ws');
  return `${wsBase}/notifications/ws/${encodeURIComponent(userId)}?token=${encodeURIComponent(token)}`;
}

interface ActiveSubscription {
  socket: WebSocket | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  visibilityHandler: (() => void) | null;
  attempts: number;
  closed: boolean;
}

const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000, 16000, 30000];

/**
 * Subscribe to the user's real-time notification stream.
 *
 * @param userId The authenticated user's ID (used in the URL path; backend
 *   ultimately trusts the JWT's `sub` claim for routing).
 * @param onMessage Called for every parsed JSON envelope received from the
 *   server. Caller is responsible for filtering envelope `type` (e.g. ignore
 *   `pong` heartbeats) and de-duplication if needed.
 * @returns Unsubscribe function — closes the socket, removes the visibility
 *   listener, clears any pending reconnect timer. Idempotent.
 */
export function subscribe(
  userId: string,
  onMessage: (payload: NotificationPayload) => void,
): () => void {
  const sub: ActiveSubscription = {
    socket: null,
    reconnectTimer: null,
    visibilityHandler: null,
    attempts: 0,
    closed: false,
  };

  const getBackoffDelay = (attempt: number): number => {
    const idx = Math.min(attempt, BACKOFF_STEPS_MS.length - 1);
    // eslint-disable-next-line security/detect-object-injection -- bounded by Math.min above; not user-controlled.
    return BACKOFF_STEPS_MS[idx];
  };

  const clearReconnect = () => {
    if (sub.reconnectTimer !== null) {
      clearTimeout(sub.reconnectTimer);
      sub.reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (sub.closed) return;
    if (typeof document !== 'undefined' && document.hidden) {
      // Tab is hidden — wait for visibilitychange to wake us up.
      return;
    }
    clearReconnect();
    const delay = getBackoffDelay(sub.attempts);
    sub.attempts += 1;
    sub.reconnectTimer = setTimeout(() => {
      sub.reconnectTimer = null;
      openSocket();
    }, delay);
  };

  const openSocket = () => {
    if (sub.closed) return;
    if (sub.socket && sub.socket.readyState <= WebSocket.OPEN) {
      // Already connecting/open — no-op.
      return;
    }

    const token = typeof window !== 'undefined' ? window.localStorage.getItem('cm_token') : null;
    if (!token) {
      // No token — can't authenticate. Stay quiet; AuthContext will resubscribe
      // after login.
      return;
    }
    const url = buildNotificationsWsUrl(userId, token);
    if (!url) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      // Construction can throw on malformed URL — schedule a retry rather than
      // bubbling a sync error to the caller. Logged for observability so we
      // notice if the URL builder regresses.
      console.warn('[notificationsService] WebSocket construction failed', err);
      scheduleReconnect();
      return;
    }
    sub.socket = ws;

    ws.onopen = () => {
      sub.attempts = 0; // reset backoff on successful open
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as NotificationPayload;
        onMessage(payload);
      } catch (err) {
        // expected: malformed JSON should never reach us — server contract
        // guarantees JSON envelopes. Log for observability so we notice if
        // the contract drifts; do not surface to user (no actionable UI).
        console.warn('[notificationsService] malformed WS message', err);
      }
    };

    ws.onerror = () => {
      // The browser will follow up with onclose; cleanup happens there.
    };

    ws.onclose = () => {
      sub.socket = null;
      if (sub.closed) return;
      scheduleReconnect();
    };
  };

  // Visibility handling — close on hidden, reopen on visible. We close the
  // socket explicitly (vs. letting the browser keep it idle) so backgrounded
  // tabs don't accumulate stale connections; the backend can then drop the
  // session entry too.
  if (typeof document !== 'undefined') {
    const handler = () => {
      if (document.hidden) {
        clearReconnect();
        if (sub.socket) {
          // Disable reconnect-on-close while we tear down deliberately.
          const s = sub.socket;
          sub.socket = null;
          s.onclose = null;
          try { s.close(); } catch { /* already closing */ }
        }
      } else {
        // Tab visible again — reset attempts for a fast first retry.
        sub.attempts = 0;
        openSocket();
      }
    };
    sub.visibilityHandler = handler;
    document.addEventListener('visibilitychange', handler);
  }

  // Initial connect (only if visible — otherwise wait for visibilitychange).
  if (typeof document === 'undefined' || !document.hidden) {
    openSocket();
  }

  return function unsubscribe() {
    if (sub.closed) return;
    sub.closed = true;
    clearReconnect();
    if (sub.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', sub.visibilityHandler);
      sub.visibilityHandler = null;
    }
    if (sub.socket) {
      const s = sub.socket;
      sub.socket = null;
      s.onclose = null; // suppress reconnect attempt
      try { s.close(); } catch { /* already closing */ }
    }
  };
}

export default { subscribe };
